<template>
  <modal-brandme :show_modal="show_modal" size="xl" body-class="p-1 insights-modal-body" @hidden="$emit('hidden')">
    <template #title>
      <span>{{ $t('campaigns.insights_modal_title') }}</span>
    </template>
    <template #body>
      <div class="mb-2" v-if="content_url">
        <p class="mb-50 font-weight-bolder" style="font-size:0.875rem">{{ $t('campaigns.link_of') }}</p>
        <div class="d-flex align-items-center border rounded p-1">
          <b-link :href="content_url" target="_blank" class="flex-shrink-0 mr-1">
            <b-img
              v-if="content_preview_url"
              :src="content_preview_url"
              width="100"
              height="56"
              style="object-fit:cover;border-radius:4px"
            />
            <div
              v-else
              style="width:100px;height:56px;background:#f0f0f0;border-radius:4px;display:flex;align-items:center;justify-content:center;"
            >
              <b-icon icon="youtube" style="font-size:24px;color:#FF0000;"/>
            </div>
          </b-link>
          <div class="flex-grow-1 overflow-hidden mr-1">
            <b-link :href="content_url" target="_blank" class="d-block text-truncate" style="font-size:0.875rem">
              {{ content_url }}
            </b-link>
          </div>
          <b-badge variant="light-primary" pill class="flex-shrink-0">
            <feather-icon icon="CheckIcon" size="11" class="mr-25"/>
            {{ $t('campaigns.insights_linked') }}
          </b-badge>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center py-4">
        <b-spinner variant="primary" />
      </div>
      <div v-else-if="!hasData" class="text-center py-4 text-muted">
        {{ $t('campaigns.insights_no_data') }}
      </div>
      <div v-else>
        <p class="text-muted mb-2" v-if="dateRangeLabel">{{ dateRangeLabel }}</p>

        <b-nav pills class="insight-mode-pills mb-2">
          <b-nav-item :active="mode === 'TOTAL'" @click="mode = 'TOTAL'">
            {{ $t('campaigns.insights_total') }}
          </b-nav-item>
          <b-nav-item :active="mode === 'ORGANIC'" @click="mode = 'ORGANIC'">
            {{ $t('campaigns.insights_organic') }}
          </b-nav-item>
        </b-nav>

        <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_performance') }}</h6>
        <b-row class="mb-3">
          <b-col v-for="stat in performanceStats" :key="stat.key" cols="12" sm="6" md="4" class="mb-1">
            <div style="position:relative">
              <statistic-card-horizontal
                :icon="stat.icon"
                :color="stat.color"
                :statistic="stat.value"
                :statistic-title="$t(stat.label)"
              />
              <span v-if="stat.tooltip" :id="`ins-perf-${stat.key}`" class="insight-tooltip-trigger">
                <feather-icon icon="InfoIcon" size="12" />
                <b-popover :target="`ins-perf-${stat.key}`" triggers="hover" placement="top">
                  <span v-html="stat.tooltip"></span>
                </b-popover>
              </span>
            </div>
          </b-col>
        </b-row>

        <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_engagement') }}</h6>
        <b-row class="mb-3">
          <b-col v-for="stat in engagementStats" :key="stat.key" cols="12" sm="6" md="4" class="mb-1">
            <div style="position:relative">
              <statistic-card-horizontal
                :icon="stat.icon"
                :color="stat.color"
                :statistic="stat.value"
                :statistic-title="$t(stat.label)"
              />
              <span v-if="stat.tooltip" :id="`ins-eng-${stat.key}`" class="insight-tooltip-trigger">
                <feather-icon icon="InfoIcon" size="12" />
                <b-popover :target="`ins-eng-${stat.key}`" triggers="hover" placement="top">
                  <span v-html="stat.tooltip"></span>
                </b-popover>
              </span>
            </div>
          </b-col>
        </b-row>

        <div class="mb-3" v-if="dailyChartSeries[0] && dailyChartSeries[0].data.length">
          <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_daily_views') }}</h6>
          <component :is="apex_charts" type="area" height="220" :options="dailyChartOptions" :series="dailyChartSeries" :key="`daily-${mode}`" />
        </div>

        <b-row class="mb-3">
          <b-col cols="12" md="5" class="mb-2">
            <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_gender') }}</h6>
            <template v-if="genderSeries.length">
              <component :is="apex_charts" type="pie" height="220" class="mt-1" :options="genderChartOptions" :series="genderSeries" :key="`gender-${mode}`" />
              <div class="pt-25">
                <div
                  v-for="(item, idx) in genderLegend"
                  :key="idx"
                  class="d-flex justify-content-between"
                  :class="idx === genderLegend.length - 1 ? '' : 'mb-1'"
                >
                  <div class="series-info">
                    <feather-icon icon="CircleIcon" size="16" class="mr-50" :style="{ color: item.color }"/>
                    <span class="font-weight-bolder">{{ item.label }}</span>
                  </div>
                  <span>{{ item.value }}%</span>
                </div>
              </div>
            </template>
            <p v-else class="text-muted small mb-0">{{ $t('campaigns.insights_no_data') }}</p>
          </b-col>
          <b-col cols="12" md="7" class="mb-2">
            <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_age') }}</h6>
            <component :is="apex_charts" type="line" height="200" :options="ageChartOptions" :series="ageChartSeries" :key="`age-${mode}`" v-if="ageChartSeries[0] && ageChartSeries[0].data.length" />
            <p v-else class="text-muted small mb-0">{{ $t('campaigns.insights_no_data') }}</p>
          </b-col>
        </b-row>

        <div class="mb-3" v-if="countries.length">
          <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_countries') }}</h6>
          <div v-for="country in countries" :key="country.regionCode" class="insight-country-item">
            <div class="d-flex justify-content-between align-items-center cursor-pointer" @click="toggleCountry(country.regionCode)">
              <span>
                <feather-icon :icon="expanded_country === country.regionCode ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" class="mr-50" v-if="country.regions && country.regions.length"/>
                {{ getCountry(country.regionCode) }}
              </span>
              <span class="text-muted">{{ formatPercent(country.percentage) }}</span>
            </div>
            <b-collapse :visible="expanded_country === country.regionCode" class="mt-1">
              <div v-for="region in country.regions" :key="region.regionCode" class="d-flex justify-content-between insight-region-item">
                <span class="text-muted small">{{ region.regionCode }}</span>
                <span class="text-muted small">{{ formatPercent(region.percentage) }}</span>
              </div>
            </b-collapse>
          </div>
        </div>

        <b-row v-if="topInMarketInterests.length || topAffinityInterests.length">
          <b-col cols="12" md="6" class="mb-2" v-if="topInMarketInterests.length">
            <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_in_market') }}</h6>
            <div v-for="interest in topInMarketInterests" :key="interest.label" class="d-flex justify-content-between insight-interest-item">
              <span>{{ interest.label }}</span>
              <span class="text-muted small">{{ formatPercent(interest.audienceSharePercentage) }}</span>
            </div>
          </b-col>
          <b-col cols="12" md="6" class="mb-2" v-if="topAffinityInterests.length">
            <h6 class="text-muted font-weight-bolder">{{ $t('campaigns.insights_affinity') }}</h6>
            <div v-for="interest in topAffinityInterests" :key="interest.label" class="d-flex justify-content-between insight-interest-item">
              <span>{{ interest.label }}</span>
              <span class="text-muted small">{{ formatPercent(interest.audienceSharePercentage) }}</span>
            </div>
          </b-col>
        </b-row>
      </div>
    </template>
  </modal-brandme>
</template>

<script>
import { BSpinner, BRow, BCol, BCollapse, BNav, BNavItem, BLink, BImg, BBadge, BPopover } from 'bootstrap-vue';
import ModalBrandme from '@/views/components/modal/ModalBrandme.vue';
import { getDatev2 } from '@/libs/utils/times';
import { separatebycomma, decimalFixed } from '@/libs/utils/formats';
import { getCountry } from '@/libs/utils/others';

const YT_OFFICIAL = '<span style="color:#FF0000;font-weight:600;font-size:12px">▶ YouTube</span><br><small>Métrica oficial de YouTube</small>';
const derived = (formula) => `<span style="color:#7367f0;font-weight:600;font-size:12px">⚙ Calculada</span><br><small><code>${formula}</code></small>`;

export default {
  name: 'InsightsModal',
  components: {
    ModalBrandme,
    BSpinner,
    BRow,
    BCol,
    BCollapse,
    BNav,
    BNavItem,
    BLink,
    BImg,
    BBadge,
    BPopover,
    StatisticCardHorizontal: () => import('@core/components/statistics-cards/StatisticCardHorizontal.vue'),
  },
  props: {
    show_modal: {
      type: Boolean,
      required: true
    },
    insights: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    content_url: {
      type: String,
      default: ''
    },
    content_preview_url: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      getCountry,
      mode: 'TOTAL',
      expanded_country: '',
      apex_charts: null,
    }
  },
  async mounted() {
    this.apex_charts = (await import('vue-apexcharts')).default;
  },
  computed: {
    hasData() {
      return !!(this.insights && this.insights.viewerPerformance);
    },
    dateRangeLabel() {
      if (!this.insights.startDate || !this.insights.endDate) return '';
      return `${getDatev2(this.dateFromParts(this.insights.startDate))} - ${getDatev2(this.dateFromParts(this.insights.endDate))}`;
    },
    performance() {
      return (this.insights.viewerPerformance && this.insights.viewerPerformance[this.mode]) || {};
    },
    engagement() {
      return (this.insights.audienceEngagement && this.insights.audienceEngagement[this.mode]) || {};
    },
    audienceProfile() {
      return (this.insights.audienceProfile && this.insights.audienceProfile[this.mode]) || {};
    },
    performanceStats() {
      return [
        { key: 'views', label: 'campaigns.insights_views', icon: 'EyeIcon', color: 'primary', value: separatebycomma(Number(this.performance.views || 0)), tooltip: YT_OFFICIAL },
        { key: 'watch', label: 'campaigns.insights_watch_time', icon: 'ClockIcon', color: 'warning', value: `${decimalFixed(this.performance.watchTimeHours || 0, 1)} h`, tooltip: YT_OFFICIAL },
        { key: 'avgdur', label: 'campaigns.insights_avg_duration', icon: 'PlayIcon', color: 'info', value: `${this.performance.avgViewDurationSecs || 0} s`, tooltip: YT_OFFICIAL },
        { key: 'avgpct', label: 'campaigns.insights_avg_percentage', icon: 'PercentIcon', color: 'success', value: `${decimalFixed(this.performance.avgViewPercentage || 0, 1)}%`, tooltip: derived('tiempo promedio de vista / duración del video × 100 (puede superar 100% si hay repeticiones)') },
        { key: 'uviewers', label: 'campaigns.insights_unique_viewers', icon: 'UsersIcon', color: 'secondary', value: separatebycomma(Number(this.performance.uniqueViewers || 0)), tooltip: YT_OFFICIAL },
        { key: 'ureach', label: 'campaigns.insights_unique_reach', icon: 'GlobeIcon', color: 'danger', value: separatebycomma(Number(this.performance.uniqueReach || 0)), tooltip: YT_OFFICIAL },
      ];
    },
    engagementStats() {
      return [
        { key: 'likes', label: 'campaigns.insights_likes', icon: 'HeartIcon', color: 'danger', value: separatebycomma(Number(this.engagement.likes || 0)), tooltip: YT_OFFICIAL },
        { key: 'comments', label: 'campaigns.insights_comments', icon: 'MessageSquareIcon', color: 'info', value: separatebycomma(Number(this.engagement.comments || 0)), tooltip: YT_OFFICIAL },
        { key: 'playlist', label: 'campaigns.insights_playlist_adds', icon: 'ListIcon', color: 'primary', value: separatebycomma(Number(this.engagement.playlistAdds || 0)), tooltip: YT_OFFICIAL },
        { key: 'shares', label: 'campaigns.insights_shares', icon: 'Share2Icon', color: 'success', value: separatebycomma(Number(this.engagement.shares || 0)), tooltip: YT_OFFICIAL },
        { key: 'dislikes', label: 'campaigns.insights_dislikes', icon: 'ThumbsDownIcon', color: 'secondary', value: separatebycomma(Number(this.engagement.dislikes || 0)), tooltip: YT_OFFICIAL },
        { key: 'rate', label: 'campaigns.insights_engagement_rate', icon: 'ActivityIcon', color: 'warning', value: this.formatPercent(this.engagement.engagementRate), tooltip: derived('(likes + comentarios) / vistas × 100') },
      ];
    },
    dailySeries() {
      return (this.insights.dailyVideoInsights || [])
        .filter(day => day.viewerPerformance && day.viewerPerformance[this.mode])
        .map(day => ({
          date: this.dateFromParts(day.insightDate),
          views: Number(day.viewerPerformance[this.mode].views || 0),
        }))
        .sort((a, b) => a.date - b.date);
    },
    dailyChartSeries() {
      return [{ name: this.$t('campaigns.insights_views'), data: this.dailySeries.map(day => day.views) }];
    },
    dailyChartOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#4be3ac'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: { categories: this.dailySeries.map(day => getDatev2(day.date)) },
        yaxis: { labels: { formatter: val => separatebycomma(Math.round(val)) } },
        tooltip: { y: { formatter: val => separatebycomma(Math.round(val)) } },
      };
    },
    genderSeries() {
      const gender = this.audienceProfile.genderDistribution;
      if (!gender) return [];
      const male = Math.round((gender.male || 0) * 100);
      const female = Math.round((gender.female || 0) * 100);
      if (male === 0 && female === 0) return [];
      return [male, female];
    },
    genderChartOptions() {
      return {
        chart: { toolbar: { show: false } },
        labels: [this.$t('campaigns.insights_male'), this.$t('campaigns.insights_female')],
        colors: ['#5cb3ff', '#E75480'],
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: { width: 4 },
        tooltip: { y: { formatter: val => `${decimalFixed(val, 1)}%` } },
      };
    },
    genderLegend() {
      if (!this.genderSeries.length) return [];
      return [
        { label: this.$t('campaigns.insights_male'), value: this.genderSeries[0], color: '#5cb3ff' },
        { label: this.$t('campaigns.insights_female'), value: this.genderSeries[1], color: '#E75480' },
      ];
    },
    ageChartSeries() {
      const age = this.audienceProfile.ageDistribution;
      if (!age) return [{ name: this.$t('campaigns.insights_age'), data: [] }];
      const data = [
        age.age18To24, age.age25To34, age.age35To44,
        age.age45To54, age.age55To64, age.age65Plus,
      ].map(val => Number(decimalFixed((val || 0) * 100, 2)));
      return [{ name: this.$t('campaigns.insights_age'), data }];
    },
    ageChartOptions() {
      return {
        chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#7367f0'],
        stroke: { curve: 'smooth', width: 4 },
        legend: { show: false },
        markers: { size: 0, hover: { size: 5 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
          axisTicks: { show: false },
          axisBorder: { show: false },
          tickPlacement: 'on',
          labels: { style: { colors: '#b9b9c3' } },
        },
        yaxis: {
          tickAmount: 4,
          labels: {
            formatter: val => `${decimalFixed(val, 2)}%`,
            style: { colors: '#b9b9c3' },
          },
        },
        grid: { borderColor: '#e7eef7' },
        tooltip: { y: { formatter: val => `${decimalFixed(val, 2)}%` } },
      };
    },
    countries() {
      return this.audienceProfile.countryDistribution || [];
    },
    topInMarketInterests() {
      return this.aggregateInterests('audienceInMarketInterestDistribution');
    },
    topAffinityInterests() {
      return this.aggregateInterests('audienceAffinityInterestDistribution');
    },
  },
  methods: {
    dateFromParts(parts) {
      return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    },
    formatPercent(value) {
      return `${decimalFixed((value || 0) * 100, 1)}%`;
    },
    toggleCountry(regionCode) {
      this.expanded_country = this.expanded_country === regionCode ? '' : regionCode;
    },
    aggregateInterests(field) {
      const acc = {};
      (this.insights.dailyVideoInsights || []).forEach(day => {
        Object.values(day[field] || {}).forEach(item => {
          if (!acc[item.label]) acc[item.label] = { label: item.label, relevanceSum: 0, shareSum: 0, count: 0 };
          acc[item.label].relevanceSum += item.relativeRelevance || 0;
          acc[item.label].shareSum += item.audienceSharePercentage || 0;
          acc[item.label].count += 1;
        });
      });
      return Object.values(acc)
        .map(item => ({
          label: item.label,
          relativeRelevance: item.relevanceSum / item.count,
          audienceSharePercentage: item.shareSum / item.count,
        }))
        .sort((a, b) => b.relativeRelevance - a.relativeRelevance)
        .slice(0, 8);
    },
  },
}
</script>

<style>
.insight-tooltip-trigger {
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  color: #adb5bd;
  line-height: 1;
  z-index: 1;
}
.insight-tooltip-trigger:hover {
  color: #6e6b7b;
}
.insights-modal-body {
  max-height: 70vh;
  overflow-y: auto;
}
.insight-mode-pills.nav-pills {
  margin-bottom: 0;
  display: inline-flex;
}
.insight-country-item {
  padding: 0.4rem 0;
  border-bottom: 1px solid #ebe9f1;
}
.insight-region-item {
  padding: 0.15rem 0 0.15rem 1.2rem;
}
.insight-interest-item {
  padding: 0.25rem 0;
  border-bottom: 1px solid #ebe9f1;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
